import { useEffect, useState } from 'react';
import axiosNest from '../../api/axiosNest';
import { hasToken, useSessionReady } from '../../hooks/useSessionReady';
import type { ExceptionRequestView } from './publicationsApi';

/**
 * Wnioski o wyjątek od bramki publikacji — klient API
 * (`apps/api/src/postings/publication-exceptions.controller.ts`; makieta back
 * office v2, fala B, 21.10.2026: BR-SAL-01, BR-TRD-04, ścieżki TRD-03
 * i MIX-03 kroki 2–4; decyzja D9 użytkownika 20.10.2026).
 *
 * Kto rozstrzyga wnioski, mówi serwer (`summary.canDecide`) — uprawnienie
 * może być nadane imiennie na koncie, czego mapa ról we froncie nie zna.
 */

/** Klucz przełącznika (rejestr `feature-flags.ts` w API). */
export const FEATURE_PUBLICATION_EXCEPTIONS = 'publicationExceptionRequests';
/** Klucz uprawnienia imiennego (dane, jak w API `shared/permission-grants.ts`). */
export const GRANT_PUBLICATION_EXCEPTION_APPROVE = 'PUBLICATION_EXCEPTION_APPROVE';
/** Nazwa uprawnienia dla operatora — jak w API. */
export const PUBLICATION_EXCEPTION_APPROVE_LABEL = 'Akceptacja wyjątku od zakazu podwykonawcy';

/** Najkrótszy powód wniosku i odrzucenia — jak w API. */
export const MIN_EXCEPTION_REASON_LENGTH = 10;

/** Zdarzenie okna „wnioski się zmieniły” — menu i sekcje odświeżają się od razu. */
export const PUBLICATION_EXCEPTIONS_CHANGED_EVENT = 'publication-exceptions-changed';
export const notifyPublicationExceptionsChanged = (): void => {
  window.dispatchEvent(new Event(PUBLICATION_EXCEPTIONS_CHANGED_EVENT));
};

export interface ExceptionQueueItem extends ExceptionRequestView {
  order: {
    id: number;
    externalId: string | null;
    fromCountry: string | null;
    fromCity: string | null;
    toCountry: string | null;
    toCity: string | null;
    fromDate: string | null;
    planningStatus: string;
  };
  contractor: { id: number; legalName: string } | null;
  canDecide: boolean;
  mine: boolean;
}

export interface ExceptionsSummary {
  canDecide: boolean;
  pendingToDecide: number;
  minePending: number;
}

const BASE = '/publication-exceptions';

export async function fetchExceptionsSummary(): Promise<ExceptionsSummary> {
  const { data } = await axiosNest.get<ExceptionsSummary>(`${BASE}/summary`);
  return data;
}

/** `status`: `pending` (domyślnie), `all` albo stan wniosku. */
export async function fetchExceptionQueue(
  status = 'pending',
): Promise<{ canDecide: boolean; items: ExceptionQueueItem[] }> {
  const { data } = await axiosNest.get(BASE, { params: { status } });
  return data;
}

export async function fetchOrderExceptions(
  userOfferId: number | string,
): Promise<{ canDecide: boolean; requests: ExceptionQueueItem[] }> {
  const { data } = await axiosNest.get(`${BASE}/order/${userOfferId}`);
  return data;
}

export async function requestException(userOfferId: number | string, reason: string): Promise<ExceptionRequestView> {
  const { data } = await axiosNest.post(`${BASE}/order/${userOfferId}`, { reason });
  return data;
}

export async function approveException(id: number, reason?: string): Promise<ExceptionRequestView> {
  const { data } = await axiosNest.post(`${BASE}/${id}/approve`, { reason: reason || undefined });
  return data;
}

export async function rejectException(id: number, reason: string): Promise<ExceptionRequestView> {
  const { data } = await axiosNest.post(`${BASE}/${id}/reject`, { reason });
  return data;
}

export async function withdrawException(id: number): Promise<ExceptionRequestView> {
  const { data } = await axiosNest.post(`${BASE}/${id}/withdraw`);
  return data;
}

/** Co odświeżać licznik — jak „Uwagi od FTL”. */
export const EXCEPTIONS_REFRESH_MS = 60_000;

/**
 * Licznik i to, czy zalogowany rozstrzyga wnioski — dla menu. Pyta API tylko
 * z sesją i przy włączonej funkcji (`enabled`); błąd = brak (menu pokazuje
 * wtedy pozycję tylko działowi Trade, bez licznika).
 */
export function useExceptionsSummary(enabled: boolean): ExceptionsSummary | null {
  const ready = useSessionReady();
  const [summary, setSummary] = useState<ExceptionsSummary | null>(null);
  useEffect(() => {
    if (!enabled || !ready) {
      setSummary(null);
      return undefined;
    }
    let active = true;
    const load = () => {
      if (!hasToken()) return;
      fetchExceptionsSummary()
        .then((s) => {
          if (active) setSummary(typeof s?.canDecide === 'boolean' ? s : null);
        })
        .catch(() => {
          if (active) setSummary(null);
        });
    };
    load();
    const timer = setInterval(load, EXCEPTIONS_REFRESH_MS);
    window.addEventListener(PUBLICATION_EXCEPTIONS_CHANGED_EVENT, load);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener(PUBLICATION_EXCEPTIONS_CHANGED_EVENT, load);
    };
  }, [enabled, ready]);
  return summary;
}

/** Wniosek jednym zdaniem — sekcja publikacji i lista. */
export function exceptionStateSentence(r: ExceptionRequestView, when: (iso: string) => string): string {
  switch (r.status) {
    case 'pending':
      return `Wniosek o wyjątek nr ${r.id} czeka na decyzję osoby z uprawnieniem „${PUBLICATION_EXCEPTION_APPROVE_LABEL}” (złożył ${r.requestedBy?.username ?? '—'}, ${when(r.createdAt)}).`;
    case 'approved':
      return `Wyjątek zatwierdzony na wniosek nr ${r.id} (zatwierdził ${r.decidedBy?.username ?? '—'}${r.decidedAt ? `, ${when(r.decidedAt)}` : ''}) — możesz zlecić publikację.`;
    case 'rejected':
      return `Wniosek o wyjątek nr ${r.id} odrzucono (${r.decidedBy?.username ?? '—'})${r.decisionReason ? `: ${r.decisionReason}` : ''}.`;
    case 'expired':
      return `Wniosek o wyjątek nr ${r.id} jest nieaktualny${r.expiryReason ? `: ${r.expiryReason}` : ''}.`;
    case 'withdrawn':
      return `Wniosek o wyjątek nr ${r.id} wycofano.`;
    default:
      return `Wniosek o wyjątek nr ${r.id}: ${r.statusLabel}.`;
  }
}
